import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { WrapperSeamless } from "layout-components";
import Table from "components/tables/table";
import { useGetList } from "hooks";
import * as Api from "service";
import AcceptModal from "components/modals/acceptModal";
import { useTranslation } from "localization/translation";
import useStore from "context";
import { ROUTES } from "Constants";
import { FeaturesInformation } from "pages/plan-details/features-information";
import { FeaturesModal } from "components/modals/planModal";

export default function HomeClients() {
  const [isOpen, setIsOpen] = useState(false);
  const { language, setRemove } = useStore();

  const [features, setFeatures] = useState([]);
  const [selectedFeature, setSelectedFeature] = useState({
    name_hy: "",
    name_en: "",
    name_ru: "",
  });
  const [isOpenFeatureModal, setIsOpenFeatureModal] = useState(false);
  const [isEditFeature, setIsEditFeature] = useState(false);
  const [loadingFeature, setLoadingFeature] = useState(false);

  const translation = useTranslation();

  const history = useHistory();

  const [selectedUser, setSelectedUser] = useState(null);
  const [query, setQuery] = useState({
    sort: "created_at",
    order: "desc",
  });

  const [items, total, refresh, setItems, loading] = useGetList(Api.main.getPlans);
  
  const routeDetails = (id) => {
    const queryParams = new URLSearchParams({ planId: id });
    history.push({
      pathname: ROUTES.plan_details,
      search: queryParams.toString(),
    });
  };

  useEffect(() => {
    if (language && `name_${language}` !== query.sort) {
      setQuery((prev) => ({
        ...prev,
        sort: `created_at`,
      }));
    }
  }, [language]);

  useEffect(() => {
    if (items?.features) {
      setFeatures(items?.features)
    }
  }, [items]);


  const openEditFeatureModal = (id) => {
    const feature = features.find(item => item.id === id);
    setSelectedFeature(feature);
    setIsOpenFeatureModal(true);
    setIsEditFeature(true);
  }

  const openAddFeatureModal = () => {
    setSelectedFeature({
      name_hy: "",
      name_en: "",
      name_ru: "",
    })
    setIsOpenFeatureModal(true);
    setIsEditFeature(false);
  }

  const closeFeatureModal = () => {
    setSelectedFeature({
      name_hy: "",
      name_en: "",
      name_ru: "",
    })
    setIsOpenFeatureModal(false);
  }

  const handleChangeFeature = (field, value) => {
    setSelectedFeature((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const deleteFeature = (featureId) => {    
    setRemove(() => () =>
      Api.main
        .deleteFeature(featureId)
        .then(() => {
          refresh();
        })   
    );
  }
 
  const handleEditFeature = () => {
    setLoadingFeature(true);
    const data = {
      name_hy: selectedFeature.name_hy,
      name_en: selectedFeature.name_en,
      name_ru: selectedFeature.name_ru,
    }
    Api.main
      .updateFeature(selectedFeature.id, data)
      .then((res) => {
        setSelectedFeature({
          name_hy: "",
          name_en: "",
          name_ru: "",
        })
        setIsOpenFeatureModal(false);
        refresh();
      })
      .finally(() => {
        setLoadingFeature(false)
      });
  }

  const handleAddFeature = () => {
    setLoadingFeature(true)
    Api.main
      .addFeature(selectedFeature)
      .then((res) => {
        setSelectedFeature({
          name_hy: "",
          name_en: "",
          name_ru: "",
        })
        setIsOpenFeatureModal(false);
        refresh();
      })
      .finally(() => {
        setLoadingFeature(false)
      });
  }

  const closeAcceptModal = () => {
    setSelectedUser(null);
    setIsOpen(false);
  };

  const setUserStatus = async (e) => {
    e.preventDefault();
    const data = {
      userId: selectedUser?.id,
      active: !selectedUser?.active,
    };

    await Api.main
      .setUserStatus(data)
      .then(() => {
        setSelectedUser(null);
        setIsOpen(false);
        refresh();
      })
      .finally(() => {
        setSelectedUser(null);
        setIsOpen(false);
      });
  };

  const rows = items?.plans?.map((item) => {
    const name = item[`name_${language}`] || item.name_en;
    const billingPeriod = [];

    const currencies = [...new Set(item.prices.map((p) => p.currency))];

    currencies.forEach((currency) => {
      const monthly = item.prices.find(
        (p) => p.currency === currency && p.billing_period === "monthly"
      )?.price;

      const annual = item.prices.find(
        (p) => p.currency === currency && p.billing_period === "annual"
      )?.price;

      if (monthly && annual) {
        billingPeriod.push(`${monthly} ${currency} / ${annual} ${currency}`);
      }
    });

    const settings = item.settings;

    return (
      <tr
        key={item.id}
        className="px-2 py-2 m-2 my-3 font-weight-bold users_list"
        onClick={() => routeDetails(item.id)}
      >
        <td className="text-left px-4">
          <div className="font-size-sm">{name}</div>
        </td>

        <td className="text-left px-4">
          <div className="font-size-sm">
            {
              billingPeriod.map((bp, index) => (
                <div key={index}>{bp}</div>
              ))
            }
          </div>
        </td>

        <td className="text-left px-4">
          {
            settings?.unlimited_items ? (
              <div className="font-size-sm">
                ∞
              </div>
            ) : (
              <div className="font-size-sm">
                {/* {settings?.max_inventory_items ?? "∞"} */}
                {settings.max_inventory_items_ml}({translation.ml}) / {settings.max_inventory_items_kg_pc}({translation.kg}/{translation.pc})
              </div>
            )
          }
        </td>

        <td className="text-left px-4">
          <div className="font-size-sm">
            {settings?.trial_period_days ?? "-"} days
          </div>
        </td>
      </tr>
    );
  });

  return (
    <>
      <WrapperSeamless>
        <Table
          fields={
            <>
              <th className="text-left px-4">
                <span className="mr-3">{translation.plan_name}</span>
              </th>
              <th className="text-left px-4">
                <span className="mr-3">{translation.plan_billing_period}</span>
              </th>
              <th className="text-left px-4">
                <span className="mr-3">{translation.max_inventory_items}</span>
              </th>
              <th className="text-left px-4">
                <span className="mr-3">{translation.trial_period_days}</span>
              </th>
            </>
          }
          noDrag
          title={translation.plans}
          items={items?.plans || []}
          setItems={setItems}
          total={total?.count}
          rows={rows}
          actions={false}
          routeDetails={routeDetails}
          userPage
        />
        <AcceptModal
          isOpen={isOpen}
          close={closeAcceptModal}
          accept={setUserStatus}
          text={translation.change_user_status}
        />
      </WrapperSeamless>
      <FeaturesInformation
        features={features}
        editHandler={openEditFeatureModal}
        deleteHandler={deleteFeature}
        showSecondButton
        showEditButton
        secondButtonText={translation.add}
        handlerSecondButton={openAddFeatureModal}
      />
      {
        !!isOpenFeatureModal && (
          <FeaturesModal
            isOpen={isOpenFeatureModal}
            close={closeFeatureModal}
            accept={(e) => {
              e.preventDefault();
              if(isEditFeature) {
                handleEditFeature()
              } else {
                handleAddFeature()
              }
            }}
            title={isEditFeature ? translation.edit_feature : translation.create_feature}
            loading={loadingFeature}
            handleChange={handleChangeFeature}
            feature={selectedFeature}
          />
        )
      }
    </>
  );
}
